import type { AppointmentCrewRole } from '@/types/jobAppointment';

/**
 * NOT-650 — crew-required job types (minCrewSize > 1) used to hard-disable
 * "Find slots" until the user hand-picked a full crew: a catch-22, because the
 * slot search is exactly the tool for discovering which N techs are free
 * together. These helpers power the fallback: search the coalesced
 * availability (per-slot `techPool` = every qualified tech free at that
 * start), keep only starts where a full crew fits, and seed an editable crew
 * from the pool when the user picks a slot.
 */

export interface CrewSeedMember {
  technicianId: string;
  role: AppointmentCrewRole;
}

export interface TechPoolSlotLike {
  /** The slot's headline technician — an explicit choice when the user picked
   * the slot through the picker's technician filter. */
  technicianId?: string;
  techPool?: Array<{ technicianId: string; technicianName: string }>;
  /**
   * The unfiltered coalesced offer. The slot picker's technician filter
   * materializes a slot by REWRITING techPool down to the chosen technician,
   * so crew seeding must read the preserved full pool instead.
   */
  fullTechPool?: Array<{ technicianId: string; technicianName: string }>;
}

function crewPool(slot: TechPoolSlotLike) {
  return slot.fullTechPool ?? slot.techPool ?? [];
}

/**
 * True when the slot search should suggest crews from the coalesced tech pool
 * instead of requiring a hand-picked crew: the job type demands a crew the
 * user has not (fully) assembled yet.
 */
export function crewSuggestionSearchActive(minCrewSize: number, crewSize: number): boolean {
  return minCrewSize > 1 && crewSize < minCrewSize;
}

/**
 * Keep only coalesced offers with enough qualified technicians free together
 * to staff the required crew. Slots without a techPool (non-coalesced) cannot
 * prove capacity and are dropped. Technicians the user already hand-picked are
 * a MUST-INCLUDE constraint: a start where any of them is busy cannot staff
 * THIS crew, so offering it would silently replace the user's pick.
 */
export function filterSlotsByCrewCapacity<T extends TechPoolSlotLike>(
  slots: T[],
  minCrewSize: number,
  mustIncludeTechnicianIds: string[] = [],
): T[] {
  if (minCrewSize <= 1) return slots;
  return slots.filter((slot) => {
    const pool = crewPool(slot);
    if (pool.length < minCrewSize) return false;
    if (mustIncludeTechnicianIds.length === 0) return true;
    const poolIds = new Set(pool.map((technician) => technician.technicianId));
    return mustIncludeTechnicianIds.every((id) => poolIds.has(id));
  });
}

/** Empty-state copy when times exist but never with a full crew free together. */
export function crewCapacityEmptyReason(minCrewSize: number, mustIncludeCount = 0): string {
  const crewLabel =
    mustIncludeCount > 0
      ? `${minCrewSize} qualified technicians — including the ${mustIncludeCount} you selected —`
      : `${minCrewSize} qualified technicians`;
  return `No times found where ${crewLabel} are free together in this range. Try widening the dates or picking the crew manually.`;
}

/**
 * Seed a crew of `minCrewSize` from a picked slot's tech pool (the preserved
 * `fullTechPool` when the picker's technician filter narrowed `techPool`).
 * Already-picked technicians who are still in the pool are kept (their lead
 * flag intact); the slot's own technician — an explicit choice on a filtered
 * pick — fills next; the remainder fills in pool order, the availability
 * engine's ranked list. Returns null when the slot cannot staff a full crew
 * (no pool / pool too small), leaving the caller's crew untouched.
 */
export function seedCrewFromSlot(
  slot: TechPoolSlotLike,
  minCrewSize: number,
  existingCrew: CrewSeedMember[],
): CrewSeedMember[] | null {
  const pool = crewPool(slot);
  if (minCrewSize <= 1 || pool.length < minCrewSize) return null;
  const poolIds = pool.map((technician) => technician.technicianId);
  const poolIdSet = new Set(poolIds);
  const kept = existingCrew.filter((member) => poolIdSet.has(member.technicianId));
  const keptIds = new Set(kept.map((member) => member.technicianId));
  const pickedId =
    slot.technicianId && poolIdSet.has(slot.technicianId) && !keptIds.has(slot.technicianId)
      ? slot.technicianId
      : null;
  const fillIds = poolIds.filter((id) => !keptIds.has(id) && id !== pickedId);
  const memberIds = [
    ...kept.map((member) => member.technicianId),
    ...(pickedId ? [pickedId] : []),
    ...fillIds,
  ].slice(0, Math.max(minCrewSize, kept.length));
  const leadId =
    kept.find((member) => member.role === 'LEAD')?.technicianId ?? memberIds[0] ?? null;
  if (!leadId) return null;
  return memberIds.map((technicianId) => ({
    technicianId,
    role: technicianId === leadId ? 'LEAD' : 'HELPER',
  }));
}
